import React from "react";
import { useMutation } from "@apollo/client";
import { gql } from "@apollo/client";
import { useDrop } from "react-dnd";
import { NativeTypes } from "react-dnd-html5-backend";
import { useSnackbar } from "notistack";

import { CircularProgress, IconButton } from "@mui/material";
import { CloudCheck as SuccessIcon, Close as CloseIcon } from "mdi-material-ui";

/**
 * Almost like fetch but with a progress callback (for file upload).
 */

function futch(url, opts = {}, onProgress = () => {}) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open(opts?.method ?? "GET", url);

    Object.entries(opts.headers ?? {}).forEach(([key, value]) => {
      xhr.setRequestHeader(key, value);
    });

    xhr.onload = () => resolve(xhr);
    xhr.onerror = reject;

    if (xhr.upload) xhr.upload.onprogress = onProgress;

    xhr.send(opts.body);
  });
}

const MUTATION = gql`
  mutation ($isPublic: Boolean!, $filepath: String!) {
    UploadFile(isPublic: $isPublic, filepath: $filepath) {
      url
      fileType
      file {
        id
        url
        isPublic
        path
        name
        metas {
          alt {
            fr
            en
          }
          legend {
            fr
            en
          }
        }
      }
    }
  }
`;

const UploadProgress = React.forwardRef(function (props, ref) {
  const [progress, setProgress] = React.useState(null);

  React.useImperativeHandle(ref, () => ({
    onProgress: (evt) => setProgress(evt.loaded / evt.total),
  }));

  return progress >= 1 ? (
    <SuccessIcon {...props} />
  ) : (
    <CircularProgress
      variant={progress == null ? "indeterminate" : "determinate"}
      value={progress * 100}
      {...props}
    />
  );
});

export function useFileUpload({ uploadPath, isPublic }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [getFileUpload] = useMutation(MUTATION);

  return React.useCallback(
    async function (file) {
      const progressRef = React.createRef();

      const snackbarKey = enqueueSnackbar(file.name, {
        persist: true,
        action: <UploadProgress ref={progressRef} size={30} color="inherit" />,
      });

      const filepath = `${uploadPath}/${file.name}`;

      try {
        const { data } = await getFileUpload({
          variables: { isPublic, filepath },
        }).catch(() => ({}));

        if (!data) return;

        await futch(
          data.UploadFile.url,
          {
            method: "PUT",
            headers: { "Content-Type": data.UploadFile.fileType },
            body: file,
          },
          (evt) => progressRef.current?.onProgress?.(evt)
        ).catch((err) => Promise.reject(<span>{err.message}</span>));

        setTimeout(() => closeSnackbar(snackbarKey), 3000);

        return data.UploadFile.file;
      } catch (err) {
        closeSnackbar(snackbarKey);

        const key = enqueueSnackbar(err, {
          variant: "error",
          persist: true,
          action: (
            <IconButton onClick={() => closeSnackbar(key)} size="large">
              <CloseIcon />
            </IconButton>
          ),
        });

        throw err;
      }
    },
    [uploadPath, isPublic, enqueueSnackbar, closeSnackbar, getFileUpload]
  );
}

export default function FileUpload(props) {
  const { multiple, uploadPath, isPublic, onUpload, children } = props;

  const uploadFile = useFileUpload({ uploadPath, isPublic });

  const upload = React.useCallback(
    (files) => {
      const doUpload = (file) =>
        uploadFile(file)
          .then((file) => onUpload?.(file))
          .catch(() => {});

      if (multiple) for (const file of files) doUpload(file);
      else doUpload(files[0]);
    },
    [uploadFile, onUpload, multiple]
  );

  const [collectedProps, drop] = useDrop({
    accept: NativeTypes.FILE,
    collect(monitor, props) {
      return {
        canDrop: monitor.canDrop(),
        isOver: monitor.isOver(),
      };
    },
    async drop(item, monitor) {
      upload(item.files);
    },
  });

  const inputProps = {
    type: "file",
    multiple,
    onChange: React.useCallback(
      (event) => upload(event.target.files),
      [upload]
    ),
  };

  return drop(children({ ...collectedProps, inputProps }));
}
